import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Minimal shape of the browser SpeechRecognition we use. Typed locally because the
 * DOM lib's `SpeechRecognition` types aren't guaranteed across our TS targets.
 */
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
}

type RecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return (w.SpeechRecognition || w.webkitSpeechRecognition || null) as RecognitionCtor | null;
}

interface Options {
  lang?: string;
  /** Called with each FINALIZED transcript chunk — append it to your input field. */
  onResult: (text: string) => void;
}

/**
 * Voice-to-text using the browser-native Web Speech API (free, on-device/cloud per
 * the browser — no backend, no key, no audio upload). `supported` is false where the
 * API is unavailable, so callers can hide the mic button rather than show a dead one.
 */
export function useSpeechRecognition({ lang = 'en-US', onResult }: Options) {
  const Ctor = getRecognitionCtor();
  const supported = !!Ctor;
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  // Keep the latest callback without re-creating start()/the recognition instance.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (!Ctor || recRef.current) return;
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = false; // final chunks only → clean append, no interim churn
    rec.onresult = (event: any) => {
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalText += event.results[i][0].transcript;
      }
      const trimmed = finalText.trim();
      if (trimmed) onResultRef.current(trimmed);
    };
    rec.onerror = () => { /* no-speech / not-allowed / aborted — onend will reset */ };
    rec.onend = () => { recRef.current = null; setListening(false); };
    recRef.current = rec;
    setListening(true);
    rec.start();
  }, [Ctor, lang]);

  const toggle = useCallback(() => {
    if (listening) stop(); else start();
  }, [listening, start, stop]);

  // Abort any in-flight recognition on unmount so it can't fire after teardown.
  useEffect(() => () => { recRef.current?.abort(); recRef.current = null; }, []);

  return { supported, listening, start, stop, toggle };
}
